import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ConfigStore } from '../../store/config.store';

/**
 * Servicio para monitorear el estado de la conexión de red.
 * Escucha eventos online/offline del navegador y expone un Observable.
 * Respeta el override `simularOffline` de ConfigStore para pruebas.
 *
 * Importante: una sola suscripción al `merge` (no repetir el observable frío por consumidor),
 * para no duplicar listeners en `window` y emisiones duplicadas al pasar offline/online.
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private readonly configStore = inject(ConfigStore);
  private readonly networkStatusSubject = new BehaviorSubject<boolean>(
    this.calcularEstadoEfectivo()
  );

  /** Misma secuencia que `getNetworkStatus()`; replay del último valor vía BehaviorSubject. */
  readonly networkStatus$: Observable<boolean> = this.networkStatusSubject.asObservable();

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    )
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        if (this.configStore.obtenerSimularOffline() === 'automatico') {
          this.actualizarEstado();
        }
      });

    this.configStore.simularOffline$
      .pipe(distinctUntilChanged())
      .subscribe(() => this.actualizarEstado());
  }

  /**
   * Obtiene el estado actual de la conexión (efectivo, con simulación si aplica).
   * @returns true si está online, false si está offline
   */
  isOnline(): boolean {
    return this.calcularEstadoEfectivo();
  }

  /**
   * Obtiene el estado actual como Observable
   * @returns Observable con el estado de la conexión
   */
  getNetworkStatus(): Observable<boolean> {
    return this.networkStatus$;
  }

  /**
   * Obtiene el valor actual del estado (síncrono)
   */
  getCurrentStatus(): boolean {
    return this.networkStatusSubject.value;
  }

  private calcularEstadoEfectivo(): boolean {
    const modo = this.configStore.obtenerSimularOffline();
    const real = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (modo === 'si') return false;
    if (modo === 'no') return true;
    return real;
  }

  private actualizarEstado(): void {
    const efectivo = this.calcularEstadoEfectivo();
    if (this.networkStatusSubject.value !== efectivo) {
      this.networkStatusSubject.next(efectivo);
    }
  }
}
